import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, Loader2, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { getDigestPreview, sendMyDigest, sendTeamDigest } from "@/lib/teamApi";
import { fmtMoney } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between py-2 border-b border-[#1F1F1F] last:border-b-0">
    <span className="kpi-label">{label}</span>
    <span className="font-mono-data text-sm text-white">{value}</span>
  </div>
);

export const DigestDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getDigestPreview()
      .then(setData)
      .catch((err) => toast.error("Could not load preview", { description: err?.response?.data?.detail || err.message }))
      .finally(() => setLoading(false));
  }, [open]);

  const onSendMine = async () => {
    setSending("mine");
    try {
      await sendMyDigest();
      toast.success("Digest sent to your inbox", { description: user?.email });
    } catch (err) {
      toast.error("Send failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSending(null);
    }
  };

  const onSendTeam = async () => {
    setSending("team");
    try {
      await sendTeamDigest();
      toast.success("Team digest sent to managers");
    } catch (err) {
      toast.error("Send failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSending(null);
    }
  };

  const mine = data?.mine;
  const team = data?.team;
  const isManager = (user?.role || "bdr") === "manager";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#141414] border border-[#262626] rounded-sm text-white p-0 overflow-hidden shadow-[0_24px_64px_-12px_rgba(0,0,0,0.8)]"
        data-testid="digest-dialog"
      >
        <DialogHeader className="px-6 py-4 border-b border-[#262626]">
          <DialogTitle className="font-display tracking-tight text-white text-xl flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#0A84FF]" strokeWidth={1.8}/>
            Weekly digest preview
          </DialogTitle>
          <div className="kpi-label">Last 7 days · {user?.email}</div>
        </DialogHeader>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#737373]">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading…
            </div>
          ) : mine ? (
            <>
              <div>
                <div className="kpi-label text-[#0A84FF] mb-2">/ YOUR WEEK</div>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                  <Row label="Pitches Submitted" value={mine.kpis.pitches_submitted}/>
                  <Row label="Meetings" value={mine.kpis.meetings}/>
                  <Row label="Proposals" value={mine.kpis.proposals}/>
                  <Row label="Wins" value={mine.kpis.wins}/>
                  <Row label="Pipeline Value" value={fmtMoney(mine.kpis.pipeline_value)}/>
                  <Row label="Activities (7d)" value={mine.kpis.activities_last_7d}/>
                </div>
              </div>

              {mine.stalled?.length > 0 && (
                <div>
                  <div className="kpi-label text-[#FF9F0A] mb-2">/ STALLED DEALS · &gt;14d IDLE</div>
                  <ul className="bg-[#0A0A0A] border border-[#FF9F0A]/30 rounded-sm divide-y divide-[#262626]" data-testid="digest-stalled-list">
                    {mine.stalled.map((s, i) => (
                      <li key={i} className="flex justify-between px-3 py-2 text-sm">
                        <span className="text-white">{s.account} <span className="text-[#737373]">· {s.stage}</span></span>
                        <span className="font-mono-data text-[#FF9F0A]">{s.days_idle}d</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {mine.at_risk?.length > 0 && (
                <div>
                  <div className="kpi-label text-[#FF453A] mb-2">/ AT-RISK · PROPOSAL &gt;21d</div>
                  <ul className="bg-[#0A0A0A] border border-[#FF453A]/30 rounded-sm divide-y divide-[#262626]" data-testid="digest-at-risk-list">
                    {mine.at_risk.map((a, i) => (
                      <li key={i} className="flex justify-between px-3 py-2 text-sm">
                        <span className="text-white">{a.account}</span>
                        <span className="font-mono-data text-[#FF453A]">{a.days}d · {fmtMoney(a.value)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {mine.next_steps?.length > 0 && (
                <div>
                  <div className="kpi-label text-[#0A84FF] mb-2">/ NEXT STEPS DUE</div>
                  <ul className="bg-[#0A0A0A] border border-[#262626] rounded-sm divide-y divide-[#262626]">
                    {mine.next_steps.map((n, i) => (
                      <li key={i} className="px-3 py-2 text-sm text-[#E5E5E5]">
                        <span className="text-white font-display tracking-tight">{n.account}</span> · {n.next_step}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {isManager && team && (
                <div className="pt-4 border-t border-[#262626]">
                  <div className="kpi-label text-[#32D74B] mb-2">/ MANAGER · TEAM ROLLUP</div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
                      <div className="kpi-label">Pipeline</div>
                      <div className="font-mono-data text-base text-white mt-1">{fmtMoney(team.summary.total_pipeline_value)}</div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
                      <div className="kpi-label">Wins $</div>
                      <div className="font-mono-data text-base text-[#32D74B] mt-1">{fmtMoney(team.summary.wins_value)}</div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
                      <div className="kpi-label">Win Rate</div>
                      <div className="font-mono-data text-base text-white mt-1">{team.summary.win_rate}%</div>
                    </div>
                  </div>
                  <div className="text-sm text-[#E5E5E5]">
                    Top BDR: <span className="font-display text-white tracking-tight">{team.summary.top_performing_bdr || "—"}</span>
                  </div>
                </div>
              )}

              {!data?.resend_configured && (
                <div className="p-3 rounded-sm bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 text-xs text-[#FF9F0A] flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0"/>
                  <span>Email provider not configured — preview only. Add RESEND_API_KEY to enable sending.</span>
                </div>
              )}
            </>
          ) : null}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#262626] bg-[#0A0A0A]/50 flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="digest-close-btn"
            className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040]"
          >
            <span className="text-xs font-mono-data tracking-wider uppercase">Close</span>
          </Button>
          {isManager && (
            <Button
              type="button"
              onClick={onSendTeam}
              disabled={sending !== null || !data?.resend_configured}
              data-testid="digest-send-team-btn"
              className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040] gap-2"
            >
              {sending === "team" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
              <span className="text-xs font-mono-data tracking-wider uppercase">Send team digest</span>
            </Button>
          )}
          <Button
            type="button"
            onClick={onSendMine}
            disabled={sending !== null || !data?.resend_configured}
            data-testid="digest-send-mine-btn"
            className="h-9 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2"
          >
            {sending === "mine" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
            <span className="text-xs font-mono-data tracking-wider uppercase">Send to me</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DigestDialog;
